import { Job, JobState, JobStatus } from './types'

function idbRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

export class JobQueueDatabase {
  private db: IDBDatabase | null = null
  private readonly version = 3

  async init(
    options: { namespace: string } = { namespace: 'web-queue' }
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(
        `queue-${options.namespace}`,
        this.version
      )

      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        this.db = request.result
        resolve()
      }

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result

        // Jobs store
        if (!db.objectStoreNames.contains('jobs')) {
          const jobStore = db.createObjectStore('jobs', {
            keyPath: 'id',
          })
          jobStore.createIndex('status', 'status', {
            unique: false,
          })
          jobStore.createIndex('queueId', 'queueId', {
            unique: false,
          })
          jobStore.createIndex('scheduledAt', 'scheduledAt', {
            unique: false,
          })
          jobStore.createIndex('priority', 'priority', {
            unique: false,
          })
        }
        if (!db.objectStoreNames.contains('stats')) {
          const stateStore = db.createObjectStore('state', {
            keyPath: 'id', // queueId-jobType
          })
          stateStore.createIndex('queueId', 'queueId', {
            unique: false,
          })
          stateStore.createIndex('type', 'type', {
            unique: false,
          })
        }
      }
    })
  }

  async addJob(job: Job): Promise<void> {
    if (!this.db) throw new Error('Database not initialized')

    const transaction = this.db.transaction(
      ['jobs'],
      'readwrite'
    )
    const store = transaction.objectStore('jobs')

    return new Promise((resolve, reject) => {
      const request = store.add(job)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  async updateJob(job: Job): Promise<void> {
    if (!this.db) throw new Error('Database not initialized')

    job.updatedAt = Date.now()
    const transaction = this.db.transaction(
      ['jobs', 'state'],
      'readwrite'
    )
    const jobsStore = transaction.objectStore('jobs')
    const stateStore = transaction.objectStore('state')
    await idbRequest(jobsStore.put(job))
    await idbRequest(
      stateStore.put({
        id: `${job.queueId}-${job.type}`,
        queueId: job.queueId,
        type: job.type,
        lastSuccess: job.updatedAt,
      })
    )
  }

  async getJob(id: string): Promise<Job | undefined> {
    if (!this.db) throw new Error('Database not initialized')

    const transaction = this.db.transaction(['jobs'], 'readonly')
    const store = transaction.objectStore('jobs')

    return new Promise((resolve, reject) => {
      const request = store.get(id)
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  async getJobsByStatus(
    status: JobStatus,
    type?: string,
    queueId?: string
  ): Promise<Job[]> {
    if (!this.db) throw new Error('Database not initialized')

    const transaction = this.db.transaction(['jobs'], 'readonly')
    const store = transaction.objectStore('jobs')
    const index = store.index('status')

    const jobs = await idbRequest<Job[]>(index.getAll(status))
    if (type || queueId) {
      return jobs.filter((job) => {
        if (type && job.type !== type) return false
        if (queueId && job.queueId !== queueId) return false
        return true
      })
    }
    return jobs
  }

  async getNextJobs(limit: number = 10): Promise<Job[]> {
    if (!this.db) throw new Error('Database not initialized')

    const transaction = this.db.transaction(['jobs'], 'readonly')
    const store = transaction.objectStore('jobs')
    const index = store.index('scheduledAt')
    const jobs: Job[] = []

    return new Promise((resolve, reject) => {
      const request = index.openCursor(null, 'prev')

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result

        if (cursor && jobs.length < limit) {
          const job = cursor.value as Job

          // Get jobs that are pending and scheduled to run
          if (
            job.status === JobStatus.PENDING &&
            job.scheduledAt <= Date.now()
          ) {
            jobs.push(job)
          }

          cursor.continue()
        } else {
          // Sort by priority (higher first) then by scheduledAt (earlier first)
          jobs.sort((a, b) => {
            if (a.priority !== b.priority) {
              return b.priority - a.priority
            }
            return a.scheduledAt - b.scheduledAt
          })

          resolve(jobs)
        }
      }

      request.onerror = () => reject(request.error)
    })
  }

  async deleteJob(id: string): Promise<void> {
    if (!this.db) throw new Error('Database not initialized')

    const transaction = this.db.transaction(
      ['jobs'],
      'readwrite'
    )
    const store = transaction.objectStore('jobs')

    return new Promise((resolve, reject) => {
      const request = store.delete(id)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  async getJobState(
    queueId: string,
    type: string
  ): Promise<JobState | undefined> {
    if (!this.db) throw new Error('Database not initialized')
    const transaction = this.db.transaction(
      ['state'],
      'readonly'
    )
    const store = transaction.objectStore('state')
    const request = store.get(`${queueId}-${type}`)
    return idbRequest<JobState>(request)
  }

  async getStats(): Promise<{ [status: string]: number }> {
    if (!this.db) throw new Error('Database not initialized')

    const transaction = this.db.transaction(['jobs'], 'readonly')
    const store = transaction.objectStore('jobs')
    const stats: { [status: string]: number } = {}

    return new Promise((resolve, reject) => {
      const request = store.openCursor()

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result

        if (cursor) {
          const job = cursor.value as Job
          stats[job.status] = (stats[job.status] || 0) + 1
          cursor.continue()
        } else {
          resolve(stats)
        }
      }

      request.onerror = () => reject(request.error)
    })
  }

  async cleanup(
    olderThan: number = 24 * 60 * 60_000 // 24 hours
  ): Promise<number> {
    if (!this.db) throw new Error('Database not initialized')

    const cutoff = Date.now() - olderThan
    const transaction = this.db.transaction(
      ['jobs'],
      'readwrite'
    )
    const store = transaction.objectStore('jobs')
    let deletedCount = 0

    return new Promise((resolve, reject) => {
      const request = store.openCursor()

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result

        if (cursor) {
          const job = cursor.value as Job

          // Delete completed or failed jobs older than cutoff
          if (
            (job.status === JobStatus.COMPLETED ||
              job.status === JobStatus.FAILED) &&
            job.updatedAt < cutoff
          ) {
            cursor.delete()
            deletedCount++
          }

          cursor.continue()
        } else {
          resolve(deletedCount)
        }
      }

      request.onerror = () => reject(request.error)
    })
  }

  async clear(): Promise<void> {
    if (!this.db) throw new Error('Database not initialized')

    const transaction = this.db.transaction(
      ['jobs', 'state'],
      'readwrite'
    )
    const jobsStore = transaction.objectStore('jobs')
    const stateStore = transaction.objectStore('state')

    return new Promise((resolve, reject) => {
      let completedOperations = 0
      const totalOperations = 2

      const checkComplete = () => {
        completedOperations++
        if (completedOperations === totalOperations) {
          console.log('Cleared JobQueueDatabase')
          resolve()
        }
      }

      const jobsClearRequest = jobsStore.clear()
      jobsClearRequest.onsuccess = () => checkComplete()
      jobsClearRequest.onerror = () =>
        reject(jobsClearRequest.error)

      const stateClearRequest = stateStore.clear()
      stateClearRequest.onsuccess = () => checkComplete()
      stateClearRequest.onerror = () =>
        reject(stateClearRequest.error)
    })
  }
}
